import React from 'react';
import { Calendar, MapPin, CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface WeekOption {
  number: number;
  date: string;
  status: 'past' | 'current' | 'future';
}

interface LeagueMatchCardProps {
  playerName: string;
  partnerName?: string;
  opponent1: string;
  opponent2?: string;
  date: string;
  time: string;
  court: string;
  score?: string;
  isPlayed?: boolean;
  modalidad: 'singles' | 'doubles';
  sport: 'tennis' | 'padel' | 'pickleball';
  title?: string;
  weeks?: WeekOption[];
  selectedWeek?: number;
  onWeekChange?: (weekNumber: number) => void;
  opponentAttendanceStatus?: 'confirmed' | 'not_attending' | 'pending';
  userAttendanceStatus?: 'confirmed' | 'not_attending' | 'pending';
  isLoadingMatch?: boolean;
}

const AttendanceBanner = ({ status, label }: { status: 'confirmed' | 'not_attending' | 'pending'; label: string }) => {
  const config = {
    confirmed: { bg: 'bg-green-50 text-green-700', icon: CheckCircle, text: label },
    not_attending: { bg: 'bg-red-50 text-red-700', icon: XCircle, text: label },
    pending: { bg: 'bg-amber-50 text-amber-700', icon: HelpCircle, text: label },
  };
  const { bg, icon: Icon, text } = config[status];
  return (
    <div className={`flex items-center justify-center text-xs font-medium rounded-lg py-1.5 px-3 ${bg}`}>
      <Icon className="h-3.5 w-3.5 mr-1.5" />
      <span>{text}</span>
    </div>
  );
};

const getUserAttendanceLabel = (status: string) => {
  if (status === 'confirmed') return 'Tu asistencia confirmada';
  if (status === 'not_attending') return 'Has indicado que no asistirás';
  return 'Confirma tu asistencia';
};

const getOpponentAttendanceLabel = (status: string) => {
  if (status === 'confirmed') return 'Asistencia oponente confirmada';
  if (status === 'not_attending') return 'Oponente no asistirá';
  return 'Asistencia oponente pendiente';
};

const LeagueMatchCard = ({
  playerName,
  partnerName,
  opponent1,
  opponent2,
  date,
  time,
  court,
  score,
  isPlayed,
  modalidad,
  sport,
  title = "Partido de la Semana",
  weeks,
  selectedWeek,
  onWeekChange,
  opponentAttendanceStatus,
  userAttendanceStatus,
  isLoadingMatch
}: LeagueMatchCardProps) => {
  const parseScore = (scoreString: string) => {
    if (!scoreString) return null;
    const sets = scoreString.split(', ');
    return sets.map(set => {
      const [team1Score, team2Score] = set.split('-');
      return { team1: team1Score, team2: team2Score };
    });
  };

  const parsedScore = isPlayed && score ? parseScore(score) : null;
  const currentWeek = weeks?.find(w => w.status === 'current');

  return (
    <div className={`rounded-xl shadow-lg p-5 transition-all duration-200 hover:shadow-xl ${
      isPlayed ? 'bg-muted/50 border-l-4 border-muted-foreground/30' : 'bg-card border-l-4 border-primary'
    }`}>
      {/* Encounter Selector */}
      {weeks && weeks.length > 0 && onWeekChange && (
        <div className="flex justify-center mb-4">
          <Select
            value={selectedWeek?.toString() || currentWeek?.number.toString() || '1'}
            onValueChange={(value) => onWeekChange(parseInt(value))}
          >
            <SelectTrigger className="w-auto h-9 text-sm px-4">
              <SelectValue placeholder="Seleccionar encuentro" />
            </SelectTrigger>
            <SelectContent>
              {weeks.map((week) => (
                <SelectItem key={week.number} value={week.number.toString()}>
                  <span className="flex items-center gap-2">
                    Encuentro {week.number}
                    {week.status === 'current' && (
                      <span className="text-xs text-primary font-medium">(Actual)</span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Loading State */}
      {isLoadingMatch && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}

      {!isLoadingMatch && (
        <>
          {/* Attendance Banners */}
          <div className="space-y-1.5 mb-4">
            {userAttendanceStatus && (
              <AttendanceBanner status={userAttendanceStatus} label={getUserAttendanceLabel(userAttendanceStatus)} />
            )}
            {opponentAttendanceStatus && (
              <AttendanceBanner
                status={opponentAttendanceStatus === 'pending' ? 'pending' : opponentAttendanceStatus}
                label={getOpponentAttendanceLabel(opponentAttendanceStatus)}
              />
            )}
          </div>

          {/* Upcoming match banner */}
          {!isPlayed && !opponentAttendanceStatus && !userAttendanceStatus && (
            <div className="mb-4 bg-primary/5 rounded-lg p-2.5">
              <div className="text-sm text-primary text-center font-medium">Próximo partido programado</div>
            </div>
          )}

          {/* Horizontal Teams Layout */}
          <div className="flex items-center justify-center gap-4 mb-4">
            {/* Team 1 */}
            <div className="flex-1 text-center min-w-0">
              <div className="font-semibold text-base text-foreground truncate">{playerName}</div>
              {partnerName && (
                <div className="text-xs text-muted-foreground truncate mt-0.5">{partnerName}</div>
              )}
              {/* Team 1 scores */}
              {parsedScore && (
                <div className="flex justify-center gap-1 mt-2">
                  {parsedScore.map((set, i) => (
                    <span key={i} className="text-sm font-bold bg-primary/10 text-primary px-2 py-0.5 rounded">
                      {set.team1}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* VS Badge */}
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-xs font-bold text-primary">VS</span>
            </div>

            {/* Team 2 */}
            <div className="flex-1 text-center min-w-0">
              <div className="font-semibold text-base text-foreground truncate">{opponent1}</div>
              {opponent2 && (
                <div className="text-xs text-muted-foreground truncate mt-0.5">{opponent2}</div>
              )}
              {/* Team 2 scores */}
              {parsedScore && (
                <div className="flex justify-center gap-1 mt-2">
                  {parsedScore.map((set, i) => (
                    <span key={i} className="text-sm font-bold bg-primary/10 text-primary px-2 py-0.5 rounded">
                      {set.team2}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Date & Court */}
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              <span>{date}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              <span>{court}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LeagueMatchCard;
